import type { MemoryArtifact, MemoryUniverse } from '../data'
import type { PermissionProfileId, StudioProject, StudioSettings } from './storage'

export type CodexAccount = {
  account?: {
    type?: string
    email?: string
    planType?: string
  } | null
  authMode?: string | null
  planType?: string | null
  requiresOpenaiAuth?: boolean
}

export type CodexModel = {
  id: string
  model?: string
  displayName?: string
  description?: string
  hidden?: boolean
  isDefault?: boolean
  defaultReasoningEffort?: string
  supportedReasoningEfforts?: Array<{
    reasoningEffort: string
    description?: string
  }>
}

export type ProviderStatus = {
  providerId: string
  state: 'unavailable' | 'needs_login' | 'ready' | 'busy' | 'rate_limited' | 'error'
  message: string
  warning?: string
  checkedAt: string
  version?: string
  executablePath?: string
}

export type ProviderDescriptor = {
  id: string
  displayName: string
  kind: string
  platform?: string
  capabilities?: string[]
  status: ProviderStatus
}

export type ProviderModel = Omit<CodexModel, 'supportedReasoningEfforts'> & {
  providerId: string
  providerModelId: string
  available: boolean
  description?: string
  isDefault?: boolean
  supportedReasoningEfforts?: string[]
}

export type RuntimePreflight = {
  schemaVersion: string
  ok: boolean
  platform: { name: string; arch: string; node: string; packaged: boolean }
  workspace: { roots: string[]; writable: boolean }
  providers: Array<{
    id: string
    displayName: string
    state: ProviderStatus['state']
    message: string
    warning?: string
    checkedAt: string
    version?: string
  }>
  security: {
    loopbackOnly: boolean
    credentialsInApp: boolean
    antigravityBundled: boolean
    tokensInMemoryPalace: boolean
  }
}

export type CodexThread = {
  id: string
  name?: string | null
  preview?: string
  cwd?: string
  model?: string
  updatedAt?: number
  createdAt?: number
  status?: unknown
  archived?: boolean
  loci?: {
    internal?: boolean
    role?: string | null
    parentThreadId?: string | null
    workflowId?: string | null
  }
}

export type CodexSkill = {
  name: string
  description?: string
  path?: string
  enabled?: boolean
  scope?: string
  interface?: {
    displayName?: string
    shortDescription?: string
  }
  configurable?: boolean
  source?: string
}

export type CodexApp = {
  id: string
  name: string
  description?: string
  logoUrl?: string | null
  logoUrlDark?: string | null
  isInstalled?: boolean
  installed?: boolean
  isAccessible?: boolean
  isEnabled?: boolean
  tools?: unknown[]
  connectionState?: string
  permissions?: unknown
}

export type RouteDecision = {
  selectedModel: string
  effort: string
  tier: string
  reasons: string[]
  escalation?: {
    nextModel?: string
    nextEffort?: string
    triggers?: string[]
  }
}

export type CodexHealth = {
  ok: boolean
  ready: boolean
  version?: string
  transport?: string
  workspaceRoots?: string[]
  environments?: {
    local: { available: boolean; reason?: string }
    worktree: { available: boolean; reason: string }
    cloud: { available: boolean; reason: string }
  }
}

export type CodexRateLimits = {
  rateLimits?: {
    planType?: string | null
    primary?: {
      usedPercent?: number
      windowDurationMins?: number
      resetsAt?: number
    } | null
    rateLimitReachedType?: string | null
  } | null
}

export type BootstrapState = {
  health: CodexHealth
  eventsUrl: string
  account: CodexAccount | null
  rateLimits: CodexRateLimits | null
  models: CodexModel[]
  threads: CodexThread[]
  skills: CodexSkill[]
  apps: CodexApp[]
}

export type TaskEventsPage = {
  taskId: string
  events: unknown[]
  cursor: string
  next: number
  hasMore?: boolean
}

export type MemoryStats = {
  files: number
  scanned?: number
  conversations: number
  relations: number
  bytesRead: number
  durationMs: number
  truncated: boolean
  reused?: number
  changed?: number
  deleted?: number
  stale?: number
  conflicts?: number
  excludedSecrets?: number
  excludedDependencies?: number
  excludedGenerated?: number
  excludedUnsupported?: number
  excludedOversize?: number
  recovered?: boolean
  lastSuccessfulIndex?: string
}

export type MemoryIndex = {
  source: 'live' | 'cache'
  indexedAt: string
  stats: MemoryStats
  universe: MemoryUniverse
  health?: MemoryHealth
}

export type MemoryContext = {
  artifacts: MemoryArtifact[]
  context: string
  approximateTokens: number
  indexedAt: string
  stats: MemoryStats
  sources?: Array<{ id: string; path: string; title: string; type: string; updatedAt: string; freshness?: string }>
}

export type MemoryHealth = {
  status: 'healthy' | 'stale' | 'missing' | 'recovered' | 'error'
  indexedAt?: string
  lastSuccessfulIndex?: string
  staleCount: number
  conflictCount?: number
  corrupted: boolean
  stats?: MemoryStats
}

export type RemotePairing = {
  pairCode: string
  pairPayload: string | null
  pairingUrl: string | null
  qrUrl: string | null
  qrFallbackUrl?: string | null
  publicGatewayConfigured?: boolean
  expiresAt: number
}
export type RemoteOperation = { id: string; kind: 'file_write' | 'terminal'; status: string; path?: string; command?: string; summary: string; result?: string; createdAt: string; updatedAt: string }
export type RemoteChatMessage = { id: string; role: 'user' | 'assistant' | 'notice'; content: string; createdAt: number; source?: string }
export type RemoteChat = { id: string; project?: string; title: string; createdAt: number; updatedAt: number; messageCount?: number; preview?: string; messages?: RemoteChatMessage[] }
export type BrowserCapabilities = { browser: { available: boolean; state: string; reason: string; actions: string[] }; computer: { available: boolean; state: string; reason: string; actions: string[] } }
export type BrowserArtifact = { id: string; type: 'screenshot' | string; label: string; mimeType: string; bytes: number; createdAt: string; url: string; path?: string; data?: string }
export type BrowserSession = { id: string; title: string; cwd: string; url: string; taskId?: string | null; state: 'ready' | 'navigating' | 'screenshotting' | 'stopped' | 'failed' | 'stale' | string; createdAt: string; updatedAt: string; lastActivityAt: string; error?: string; artifacts: BrowserArtifact[] }

export type ProjectFile = {
  path: string
  name: string
  type: 'file' | 'directory'
  size: number | null
  modifiedAt: number
  depth: number
}

export type ProjectFilePreview = {
  path: string
  content: string
  size: number
  modifiedAt: number
}

export type ProjectChange = {
  status: string
  path: string
  indexStatus?: string
  worktreeStatus?: string
  binary?: boolean
}

export type ProjectDiff = {
  path: string | null
  staged: boolean
  diff: string
  binary: boolean
  truncated: boolean
  bytes: number
}

export type WorkflowStep = {
  id: string
  title: string
  description?: string
  files?: string[]
  checks?: string[]
  commands?: string[]
  status: string
  attempts: number
  retries: number
  retryCap: number
  workerResult?: { ok: boolean; summary: string; recordedAt?: string } | null
  review?: { decision: string; summary: string; recordedAt?: string } | null
  execution?: ProviderSelection | null
  currentExecution?: ProviderSelection & { attempt?: number; idempotencyKey?: string; status?: string; startedAt?: string }
}

export type WorkflowPlan = {
  id: string
  title: string
  state: string
  version: number
  createdAt: string
  updatedAt: string
  currentStepId?: string | null
  resumeState?: string | null
  retryCap: number
  steps: WorkflowStep[]
  metadata?: {
    cwd?: string
    task?: string
    supervisorProviderId?: string
    supervisorModel?: string
    supervisorSelection?: ProviderSelection
    workerSelection?: ProviderSelection
    reviewerSelection?: ProviderSelection
    supervisorOutput?: string
    planSummary?: string
    lastPlanRevision?: string
    planningStatus?: 'queued' | 'running' | 'ready' | 'failed'
    planningError?: string
    planningStartedAt?: string
    planningCompletedAt?: string
    teamMode?: boolean
  }
  ledger: Array<{ at: string; from: string; to: string; event: string; stepId?: string; summary?: string }>
}

export type AgentLane = {
  id: string
  parentTaskId: string
  role: 'supervisor' | 'worker' | 'reviewer' | 'specialist' | 'side-chat' | string
  title: string
  providerId: string
  model: string
  effort: string
  profileId?: string | null
  displayName?: string
  roleLabel?: string
  lead?: boolean
  avatar?: { asset?: string; accent?: string; background?: string } | null
  assignedStepId?: string | null
  dependencies?: string[]
  state: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'blocked' | string
  statusSummary?: string
  result?: { summary?: string; files?: string[] } | null
  error?: string | null
  startedAt?: string | null
  completedAt?: string | null
  updatedAt?: string | null
}

export type AgentGraph = {
  id: string
  parentTaskId: string
  title: string
  state: string
  maxConcurrent: number
  budget: number
  spent: number
  createdAt: string
  updatedAt: string
  version: number
  roomSequence?: number
  agents: AgentLane[]
  messages: Array<{
    id: string
    taskId?: string | null
    sequence: number
    from: { type: 'user' | 'agent' | 'system'; id: string }
    fromLabel?: string
    to: { mode: 'lead' | 'agent' | 'agents' | 'room'; ids: string[] }
    kind: string
    text: string
    preview: string
    at?: string | null
    createdAt?: string | null
    agentId?: string | null
  }>
}

export type TeamProfile = {
  id: string
  displayName: string
  genderPresentation?: 'male' | 'female'
  pronouns?: string
  role: string
  specialty: string
  summary: string
  lead?: boolean
  avatar?: { asset?: string; accent?: string; background?: string; artDirection?: string }
  permissions?: Record<string, string | boolean>
  outputKinds?: string[]
}

export type TeamRoomResponse = { workflow: WorkflowPlan; graph: AgentGraph }

export type TerminalSession = {
  id: string
  taskId: string
  cwd: string
  command: string | string[]
  tty: boolean
  placement: 'bottom' | 'right' | 'floating'
  processId: string | null
  status: 'starting' | 'running' | 'exited' | 'terminated' | 'failed'
  exitCode: number | null
  truncated: boolean
  sequence?: number
  createdAt: string
  updatedAt: string
  outputBytes: number
}

export type TerminalOutput = {
  sessionId: string
  taskId: string
  stream?: 'stdout' | 'stderr'
  text?: string
  sequence?: number
  truncated?: boolean
  exitCode?: number | null
  status?: string
  reason?: string | null
}

export type ProviderSelection = {
  providerId: string
  model: string
  effort: string
  timeoutMs: number
}

const API_BASE = '/api/codex'
let sessionToken: string | null = null

async function createSession(): Promise<string> {
  const response = await fetch(`${API_BASE}/session`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: '{}',
  })
  const payload = await response.json().catch(() => ({})) as { sessionToken?: string; error?: string }
  if (!response.ok || !payload.sessionToken) {
    throw new Error(payload.error || 'Güvenli yerel oturum başlatılamadı.')
  }
  sessionToken = payload.sessionToken
  return sessionToken
}

async function ensureSession(): Promise<string> {
  return sessionToken ?? createSession()
}

async function request<T>(path: string, init?: RequestInit, retry = true): Promise<T> {
  const token = await ensureSession()
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      'X-LOCI-Session': token,
      ...init?.headers,
    },
  })
  if (response.status === 401 && retry) {
    sessionToken = null
    return request<T>(path, init, false)
  }
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    const message = typeof payload?.error === 'string'
      ? payload.error
      : typeof payload?.message === 'string'
        ? payload.message
        : `İstek başarısız oldu (${response.status})`
    throw new Error(message)
  }
  return payload as T
}

function unwrapList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[]
  if (!payload || typeof payload !== 'object') return []
  const record = payload as Record<string, unknown>
  if (Array.isArray(record.data)) return record.data as T[]
  if (record.result && typeof record.result === 'object') {
    const nested = record.result as Record<string, unknown>
    if (Array.isArray(nested.data)) return nested.data as T[]
  }
  return []
}

function unwrapSkills(payload: unknown): CodexSkill[] {
  const data = unwrapList<Record<string, unknown>>(payload)
  return data.flatMap((entry) => Array.isArray(entry.skills) ? entry.skills as CodexSkill[] : entry as unknown as CodexSkill)
}

export async function loadBootstrap(cwd: string): Promise<BootstrapState> {
  const token = await ensureSession()
  const eventsUrl = `${API_BASE}/events?session=${encodeURIComponent(token)}`
  const health = await request<CodexHealth>('/health')
  if (!health.ready) {
    return { health, eventsUrl, account: null, rateLimits: null, models: [], threads: [], skills: [], apps: [] }
  }

  const encodedCwd = encodeURIComponent(cwd)
  const [account, rateLimits, modelPayload, threadPayload, skillPayload, appPayload] = await Promise.all([
    request<CodexAccount>('/account').catch(() => null),
    request<CodexRateLimits>('/rate-limits').catch(() => null),
    request<unknown>('/models?limit=100&includeHidden=false').catch(() => ({ data: [] })),
    request<unknown>(`/threads?cwd=${encodedCwd}&limit=50&archived=false`).catch(() => ({ data: [] })),
    request<unknown>(`/skills?cwd=${encodedCwd}`).catch(() => ({ data: [] })),
    request<unknown>('/apps?limit=100').catch(() => ({ data: [] })),
  ])

  return {
    health,
    eventsUrl,
    account,
    rateLimits,
    models: unwrapList<CodexModel>(modelPayload),
    threads: unwrapList<CodexThread>(threadPayload),
    skills: unwrapSkills(skillPayload),
    apps: unwrapList<CodexApp>(appPayload),
  }
}

export const codexApi = {
  syncProjects: (projects: StudioProject[], activeProjectId: string, settings: StudioSettings) => request<{ projects: StudioProject[]; activeProjectId: string; settings: StudioSettings; updatedAt: number }>('/projects/sync', {
    method: 'POST',
    body: JSON.stringify({ projects, activeProjectId, settings }),
  }),
  remotePairing: () => request<RemotePairing>('/remote/pairing'),
  remoteOperations: () => request<{ operations: RemoteOperation[] }>('/remote/operations'),
  remoteChats: () => request<{ chats: RemoteChat[] }>('/remote/chats'),
  remoteChat: (chatId: string) => request<{ chat: RemoteChat }>(`/remote/chats/${encodeURIComponent(chatId)}`),
  decideRemoteOperation: (id: string, decision: 'approve' | 'decline') => request<{ operation: RemoteOperation }>(`/remote/operations/${encodeURIComponent(id)}/${decision}`, { method: 'POST', body: '{}' }),
  browserCapabilities: () => request<BrowserCapabilities>('/browser/capabilities'),
  browserSessions: () => request<{ sessions: BrowserSession[] }>('/browser/sessions'),
  openBrowserSession: (params: { cwd: string; url: string; title?: string; approval: boolean; taskId?: string; threadId?: string; workflowId?: string }) => request<{ session: BrowserSession }>('/browser/sessions', { method: 'POST', body: JSON.stringify(params) }),
  navigateBrowser: (id: string, url: string, approval = false, taskId?: string) => request<{ session: BrowserSession }>(`/browser/sessions/${encodeURIComponent(id)}/navigate`, { method: 'POST', body: JSON.stringify({ url, approval, taskId }) }),
  screenshotBrowser: (id: string, label?: string, taskId?: string) => request<{ artifact: BrowserArtifact }>(`/browser/sessions/${encodeURIComponent(id)}/screenshot`, { method: 'POST', body: JSON.stringify({ label, taskId }) }),
  stopBrowser: (id: string, reason?: string, taskId?: string) => request<{ session: BrowserSession }>(`/browser/sessions/${encodeURIComponent(id)}/stop`, { method: 'POST', body: JSON.stringify({ reason, taskId }) }),
  projectFiles: (cwd: string) => request<{ files: ProjectFile[]; truncated: boolean }>(`/files?cwd=${encodeURIComponent(cwd)}`),
  projectFile: (cwd: string, path: string) => request<ProjectFilePreview>(`/file?cwd=${encodeURIComponent(cwd)}&path=${encodeURIComponent(path)}`),
  projectChanges: (cwd: string) => request<{ repository: boolean; changes: ProjectChange[] }>(`/changes?cwd=${encodeURIComponent(cwd)}`),
  projectDiff: (cwd: string, path?: string | null, staged = false, context = 3) => request<ProjectDiff>(`/diff?cwd=${encodeURIComponent(cwd)}${path ? `&path=${encodeURIComponent(path)}` : ''}&staged=${staged ? 'true' : 'false'}&context=${context}`),
  gitAction: (params: { cwd: string; action: 'stage' | 'unstage' | 'revert' | 'commit'; paths?: string[]; message?: string; confirm?: boolean }) => request<{ repository: boolean; changes: ProjectChange[] }>('/git/action', {
    method: 'POST',
    body: JSON.stringify(params),
  }),
  route: (task: string, plan?: string) => request<RouteDecision>('/route', {
    method: 'POST',
    body: JSON.stringify({ task, plan }),
  }),
  createWorkflow: (params: { cwd: string; task: string; retryCap?: number; supervisor?: ProviderSelection; worker?: ProviderSelection; reviewer?: ProviderSelection; teamMode?: boolean }) => request<{ workflow: WorkflowPlan }>('/workflows', {
    method: 'POST',
    body: JSON.stringify(params),
  }),
  startTeamConversation: (params: { cwd: string; task: string; selection: ProviderSelection; agentId?: string; conversationId?: string }) => request<{ plan: null; conversationId: string; graph: AgentGraph; message: AgentGraph['messages'][number] }>('/team-conversations', {
    method: 'POST',
    body: JSON.stringify(params),
  }),
  listWorkflows: (cwd: string) => request<{ workflows: WorkflowPlan[] }>(`/workflows?cwd=${encodeURIComponent(cwd)}`),
  readWorkflow: (id: string) => request<{ workflow: WorkflowPlan }>(`/workflows/${encodeURIComponent(id)}`),
  approveWorkflow: (id: string, note?: string) => request<{ workflow: WorkflowPlan }>(`/workflows/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  }),
  reviseWorkflow: (id: string, instruction: string) => request<{ workflow: WorkflowPlan }>(`/workflows/${encodeURIComponent(id)}/revise`, {
    method: 'POST',
    body: JSON.stringify({ instruction }),
  }),
  pauseWorkflow: (id: string, reason?: string) => request<{ workflow: WorkflowPlan }>(`/workflows/${encodeURIComponent(id)}/pause`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),
  resumeWorkflow: (id: string) => request<{ workflow: WorkflowPlan }>(`/workflows/${encodeURIComponent(id)}/resume`, { method: 'POST', body: '{}' }),
  cancelWorkflow: (id: string, reason?: string) => request<{ workflow: WorkflowPlan }>(`/workflows/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),
  workflowAgents: (id: string) => request<{ graph: AgentGraph }>(`/workflows/${encodeURIComponent(id)}/agents`),
  messageWorkflowAgent: (id: string, message: string, agentId?: string) => request<{ graph: AgentGraph; message: AgentGraph['messages'][number] }>(`/workflows/${encodeURIComponent(id)}/agents${agentId ? `/${encodeURIComponent(agentId)}` : ''}`, {
    method: 'POST', body: JSON.stringify({ message, agentId }),
  }),
  cancelWorkflowAgents: (id: string, reason?: string) => request<{ graph: AgentGraph }>(`/workflows/${encodeURIComponent(id)}/agents`, {
    method: 'POST', body: JSON.stringify({ action: 'cancel', reason }),
  }),
  teamProfiles: () => request<{ profiles: TeamProfile[] }>('/team/profiles'),
  teamRoom: (id: string) => request<TeamRoomResponse>(`/workflows/${encodeURIComponent(id)}/team`),
  teamMessage: (id: string, input: { message: string; agentId?: string; to?: { mode: 'lead' | 'agent' | 'agents' | 'room'; ids: string[] }; kind?: string; replyTo?: string; decisionRefs?: string[]; contextRefs?: string[] }) => request<{ message: AgentGraph['messages'][number]; graph: AgentGraph }>(`/workflows/${encodeURIComponent(id)}/team/messages`, {
    method: 'POST', body: JSON.stringify(input),
  }),
  summonTeamAgent: (id: string, input: { profileId: string; assignmentId?: string; stepId?: string; scope?: string; mode?: 'read' | 'write'; message?: string; actorId?: 'adrian' }) => request<{ agent?: AgentLane; message?: AgentGraph['messages'][number]; graph: AgentGraph }>(`/workflows/${encodeURIComponent(id)}/team/summon`, {
    method: 'POST', body: JSON.stringify(input),
  }),
  dismissTeamAgent: (id: string, agentId: string, reason?: string) => request<{ agent: AgentLane; graph: AgentGraph }>(`/workflows/${encodeURIComponent(id)}/team/agents/${encodeURIComponent(agentId)}/dismiss`, {
    method: 'POST', body: JSON.stringify({ reason }),
  }),
  recordTeamDecision: (id: string, input: { scope: string[]; statement: string; value?: unknown; sourceMessageId?: string; affectedAgents?: string[]; affectedFiles?: string[]; supersedes?: string[] }) => request<{ decision: Record<string, unknown>; workflow: WorkflowPlan; graph: AgentGraph }>(`/workflows/${encodeURIComponent(id)}/team/decisions`, {
    method: 'POST', body: JSON.stringify(input),
  }),
  resolveTeamConflict: (id: string, conflictId: string, input: { decisionId?: string; note?: string } = {}) => request<TeamRoomResponse>(`/workflows/${encodeURIComponent(id)}/team/conflicts/${encodeURIComponent(conflictId)}/resolve`, {
    method: 'POST', body: JSON.stringify(input),
  }),
  respondTeamQuestion: (id: string, questionId: string, message: string) => request<{ message: AgentGraph['messages'][number]; graph: AgentGraph }>(`/workflows/${encodeURIComponent(id)}/team/questions/${encodeURIComponent(questionId)}/respond`, {
    method: 'POST', body: JSON.stringify({ message }),
  }),
  memoryIndex: (cwd: string, force = false) => request<MemoryIndex>(force ? '/memory/index' : `/memory?cwd=${encodeURIComponent(cwd)}`, force ? {
    method: 'POST',
    body: JSON.stringify({ cwd, force }),
  } : undefined),
  memoryContext: (cwd: string, query: string) => request<MemoryContext>('/memory/context', {
    method: 'POST',
    body: JSON.stringify({ cwd, query }),
  }),
  memoryHealth: (cwd: string) => request<MemoryHealth>(`/memory/health?cwd=${encodeURIComponent(cwd)}`),
  deleteMemoryArtifact: (cwd: string, artifactId: string) => request<{ ok: boolean; memory: MemoryIndex }>('/memory/delete', {
    method: 'POST',
    body: JSON.stringify({ cwd, artifactId }),
  }),
  captureMemory: (cwd: string, threadId: string) => request<MemoryIndex>('/memory/capture', {
    method: 'POST',
    body: JSON.stringify({ cwd, threadId }),
  }),
  createThread: (params: {
    cwd: string
    model: string
    effort?: string
    personality?: string
    permissionProfile?: PermissionProfileId
    fullAccessConfirmed?: boolean
    internal?: boolean
    internalRole?: string
    parentThreadId?: string
  }) => request<{ thread: CodexThread }>('/threads', {
    method: 'POST',
    body: JSON.stringify(params),
  }),
  resumeThread: (threadId: string, params: { cwd?: string; model?: string; effort?: string } = {}) => request<{ thread: CodexThread }>(`/threads/${encodeURIComponent(threadId)}/resume`, {
    method: 'POST',
    body: JSON.stringify(params),
  }),
  readThread: (threadId: string) => request<unknown>(`/threads/${encodeURIComponent(threadId)}?includeTurns=true`),
  taskEvents: (taskId: string, after = -1, limit = 500) => request<TaskEventsPage>(`/task-events?taskId=${encodeURIComponent(taskId)}&after=${after}&limit=${Math.min(500, Math.max(1, limit))}`),
  startTurn: (threadId: string, params: {
    text: string
    input?: Array<Record<string, unknown>>
    model?: string
    effort?: string
    summary?: string
    cwd?: string
    permissionProfile?: PermissionProfileId
    fullAccessConfirmed?: boolean
  }) => request<{ turn?: { id?: string }; routing?: RouteDecision }>(`/threads/${encodeURIComponent(threadId)}/turns`, {
    method: 'POST',
    body: JSON.stringify(params),
  }),
  runProviderTurn: (params: {
    providerId: string
    model: string
    effort?: string
    cwd: string
    prompt: string
  }) => request<{ result: { providerId: string; model: string; status: string; text: string; conversationId?: string } }>('/provider-turns', {
    method: 'POST',
    body: JSON.stringify(params),
  }),
  interrupt: (threadId: string, turnId: string) => request<unknown>(`/threads/${encodeURIComponent(threadId)}/interrupt`, {
    method: 'POST',
    body: JSON.stringify({ turnId }),
  }),
  steer: (threadId: string, turnId: string, input: Array<Record<string, unknown>>) => request<{ turnId: string }>(`/threads/${encodeURIComponent(threadId)}/steer`, {
    method: 'POST',
    body: JSON.stringify({ turnId, input }),
  }),
  startReview: (threadId: string, target: { type: 'uncommittedChanges' | 'baseBranch' | 'commit' | 'custom'; branch?: string; sha?: string; title?: string; instruction?: string }, delivery: 'inline' | 'detached' = 'inline') => request<{ turn?: { id?: string }; reviewThreadId?: string }>(`/threads/${encodeURIComponent(threadId)}/review`, {
    method: 'POST',
    body: JSON.stringify({ delivery, target }),
  }),
  forkThread: (threadId: string, params: { lastTurnId?: string; ephemeral?: boolean } = {}) => request<{ thread: CodexThread }>(`/threads/${encodeURIComponent(threadId)}/fork`, {
    method: 'POST',
    body: JSON.stringify(params),
  }),
  terminals: (taskId: string) => request<{ terminals: TerminalSession[] }>(`/terminals?taskId=${encodeURIComponent(taskId)}`),
  terminalOutput: (id: string, limit = 65_536) => request<{ session: TerminalSession; output: string; truncated: boolean }>(`/terminals/${encodeURIComponent(id)}/output`, {
    method: 'POST',
    body: JSON.stringify({ authorized: true, limit }),
  }),
  createTerminal: (params: { taskId: string; cwd: string; command: string; tty?: boolean; placement?: TerminalSession['placement'] }) => request<{ session: TerminalSession } | { session: TerminalSession; result?: unknown }>('/terminals', {
    method: 'POST',
    body: JSON.stringify(params),
  }),
  writeTerminal: (id: string, data: string, closeStdin = false) => request<{ terminal: TerminalSession }>(`/terminals/${encodeURIComponent(id)}/write`, {
    method: 'POST',
    body: JSON.stringify({ data, closeStdin }),
  }),
  resizeTerminal: (id: string, rows: number, cols: number) => request<{ terminal: TerminalSession }>(`/terminals/${encodeURIComponent(id)}/resize`, {
    method: 'POST',
    body: JSON.stringify({ rows, cols }),
  }),
  stopTerminal: (id: string) => request<{ terminal: TerminalSession }>(`/terminals/${encodeURIComponent(id)}/stop`, {
    method: 'POST',
    body: JSON.stringify({ reason: 'terminated_by_user' }),
  }),
  clearTerminal: (id: string) => request<{ terminal: TerminalSession }>(`/terminals/${encodeURIComponent(id)}/clear`, {
    method: 'POST',
    body: '{}',
  }),
  cancelExecution: (executionId: string, reason = 'cancelled_by_user') => request<{ execution: { id: string; state: string; error?: string | null } }>(`/executions/${encodeURIComponent(executionId)}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),
  executions: () => request<{ executions: Array<{ id: string; taskId?: string; state: string; error?: string | null; updatedAt?: string }> }>('/executions'),
  startChatGptLogin: (mode: 'browser' | 'device' = 'browser') => request<{
    type: string
    loginId: string
    authUrl?: string
    verificationUrl?: string
    userCode?: string
  }>('/auth/chatgpt/start', {
    method: 'POST',
    body: JSON.stringify({ mode }),
  }),
  providers: () => request<{ providers: ProviderDescriptor[] }>('/providers'),
  providerModels: (includeHidden = false) => request<{ data: ProviderModel[]; providers: ProviderDescriptor[]; source: string }>(`/provider-models?includeHidden=${includeHidden ? 'true' : 'false'}`),
  preflight: () => request<RuntimePreflight>('/preflight'),
  setSkillEnabled: (path: string, enabled: boolean) => request<unknown>('/skills/config', {
    method: 'POST',
    body: JSON.stringify({ path, enabled }),
  }),
  respondRequest: (requestId: string | number, result: unknown, error?: unknown) => request<unknown>(`/requests/${encodeURIComponent(String(requestId))}/respond`, {
    method: 'POST',
    body: JSON.stringify({ result, ...(error ? { error } : {}) }),
  }),
}
